import { useEffect, useMemo } from 'react';
import type {
  FieldError,
  FieldErrors,
  UseFormReturn,
} from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { LevelOfCareContext } from './machines/levelOfCare.machine';
import {
  createReservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';
import { useReservationFormMachines } from './ReservationFormMachinesContext';
import { useFieldActorSnapshot } from './useFieldActorSnapshot';
import { getAggregatedFormValues } from './utils/getAggregatedFormValues';

function useDebugReservationMachines(
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>,
) {
  const control = form.control;
  const { t } = useTranslation('common');
  const watchedTrips = form.watch('trips');
  const { mainActor, mainSnapshot, fieldRefs } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const isNonAmbulatoryByIndex = (
    levelOfCareSnap.context as LevelOfCareContext
  ).isNonAmbulatoryByIndex;

  const tripLegStepSchema = useMemo(
    () => createReservationFormSchema(null, t),
    [t],
  );
  const memberStepSchema = useMemo(
    () => createReservationFormSchema(null, t, isNonAmbulatoryByIndex),
    [t, isNonAmbulatoryByIndex],
  );

  const mergedValues = useMemo(() => {
    const aggregatedValues = getAggregatedFormValues(mainSnapshot.context);
    return {
      ...aggregatedValues,
      trips: aggregatedValues.trips.map((mt, i) => ({
        ...mt,
        ...(watchedTrips?.[i] ?? {}),
      })),
    } as ReservationFormValues;
  }, [mainSnapshot.context, watchedTrips]);

  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');
  const hasServerValidationErrors =
    (mainSnapshot.context.lastValidationResult?.errors?.length ?? 0) > 0;

  const tripLegParseResult = tripLegStepSchema.safeParse(mergedValues);
  const memberParseResult = memberStepSchema.safeParse(mergedValues);

  const { errors, isValid } = useFormState({ control });

  useEffect(() => {
    const logMain = () => {
      const s = mainActor.getSnapshot();
      console.debug('[reservationFormMain]', s.value, s.context);
    };

    const unsubMain = mainActor.subscribe((snapshot) => {
      console.debug('[reservationFormMain]', snapshot.value, snapshot.context);
    });

    const childEntries = Object.entries(fieldRefs) as [
      keyof typeof fieldRefs,
      (typeof fieldRefs)[keyof typeof fieldRefs],
    ][];

    const unsubsChildren = childEntries.map(([name, ref]) =>
      ref.subscribe((snapshot) => {
        console.debug(`[${String(name)}]`, snapshot.value, snapshot.context);
      }),
    );

    logMain();

    return () => {
      unsubMain.unsubscribe();
      unsubsChildren.forEach((u) => u.unsubscribe());
    };
  }, [mainActor, fieldRefs]);

  useEffect(() => {
    console.debug('[reservationForm]', {
      isValid,
      zodErrors: errors,
      mergedValues,
      nextButton: [
        {
          page: 'Trip Leg Form',
          enabled:
            !isSubmitting &&
            tripLegParseResult.success &&
            !hasServerValidationErrors,
          isSubmitting,
          zodValid: tripLegParseResult.success,
          hasServerValidationErrors,
        },
        {
          page: 'Member Form',
          enabled: !isSubmitting && memberParseResult.success,
          isSubmitting,
          zodValid: memberParseResult.success,
        },
      ],
    });
  }, [
    errors,
    isValid,
    mergedValues,
    isSubmitting,
    tripLegParseResult.success,
    hasServerValidationErrors,
    memberParseResult.success,
  ]);
}

function ReservationFormDebug({
  form,
}: {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
}) {
  useDebugReservationMachines(form);
  return null;
}

export {
  useDebugReservationMachines,
  ReservationFormDebug,
};
